import { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Share2, Loader2, X, Copy, ExternalLink, Check, Link2 } from 'lucide-react';
import { PLATFORM_ICONS, PLATFORM_COLORS } from '../pages/SocialSyncPage';

export default function ShareToSocialButton({ contentType, contentId, contentTitle, caption, size = 'sm' }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [showPanel, setShowPanel] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [results, setResults] = useState(null);

  const openPanel = async () => {
    setShowPanel(true);
    if (connections.length > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/social/connections`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setConnections(d.connections || []); }
    } catch {}
    finally { setLoading(false); }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const platforms = connections.map(c => c.platform);
      const res = await fetch(`${API}/social/cross-post`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: contentId,
          platforms,
          default_caption: caption || contentTitle || '',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
        const posted = (data.results || []).filter(r => r.status === 'posted').length;
        const smartShare = (data.results || []).filter(r => r.status === 'smart_share').length;
        if (posted > 0) toast.success(`Shared to ${posted} platform(s)!`);
        if (smartShare > 0) toast.info(`${smartShare} platform(s) ready to share`);
      } else {
        toast.error('Share failed');
      }
    } catch { toast.error('Share failed'); }
    finally { setSharing(false); }
  };

  const handleSmartShare = (result) => {
    if (result.share_url) {
      window.open(result.share_url, '_blank', 'width=600,height=400');
    } else {
      const text = `${result.caption || ''}\n\n${result.post_url || ''}`.trim();
      navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => toast.error('Copy failed'));
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/post/${contentId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied!')).catch(() => toast.error('Copy failed'));
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      <Button
        variant="ghost" size={size}
        onClick={(e) => { e.stopPropagation(); openPanel(); }}
        className="text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10"
        data-testid={`share-social-btn-${contentId}`}
      >
        <Share2 className="w-3.5 h-3.5 mr-1" />Share
      </Button>

      {showPanel && (
        <div className="fixed inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowPanel(false); setResults(null); }}>
          <Card className="w-full max-w-sm bg-card border-border p-4" onClick={e => e.stopPropagation()}
            data-testid="share-social-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm text-white">Share to Social</h3>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => { setShowPanel(false); setResults(null); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Copy Link */}
            <button onClick={copyLink}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border hover:border-border transition-all mb-3"
              data-testid="copy-link-btn">
              <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                <Link2 className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-foreground/90">Copy Link</span>
              <Copy className="w-4 h-4 text-muted-foreground ml-auto" />
            </button>

            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
              </div>
            ) : connections.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                No connected platforms.{' '}
                <a href="/social-sync" className="text-blue-400 hover:underline">Connect accounts</a>
              </p>
            ) : results ? (
              <div className="space-y-2">
                {results.map(r => (
                  <div key={r.cross_post_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20">
                    <div className={`w-7 h-7 rounded-md ${PLATFORM_COLORS[r.platform] || 'bg-muted'} flex items-center justify-center text-white scale-75`}>
                      {PLATFORM_ICONS[r.platform]}
                    </div>
                    <span className="text-sm flex-1">{r.platform}</span>
                    {r.status === 'posted' && (
                      <span className="text-[10px] text-green-400 flex items-center gap-1"><Check className="w-3 h-3" />Posted</span>
                    )}
                    {r.status === 'smart_share' && (
                      <Button size="sm" variant="ghost" className="text-[10px] text-blue-400 h-6 px-2"
                        onClick={() => handleSmartShare(r)}>
                        <ExternalLink className="w-3 h-3 mr-1" />Share
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-1.5 mb-3">
                  {connections.map(conn => (
                    <div key={conn.connection_id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-muted/20 border border-border">
                      <div className={`w-7 h-7 rounded-md ${PLATFORM_COLORS[conn.platform] || 'bg-muted'} flex items-center justify-center text-white scale-75`}>
                        {PLATFORM_ICONS[conn.platform]}
                      </div>
                      <span className="text-sm flex-1">{conn.account_name || conn.platform}</span>
                      <span className={`text-[10px] ${conn.status === 'connected' ? 'text-green-400' : 'text-blue-400'}`}>
                        {conn.status === 'connected' ? 'Auto' : 'Share'}
                      </span>
                    </div>
                  ))}
                </div>
                <Button onClick={handleShare} disabled={sharing}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="share-all-btn">
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                  Share to All ({connections.length})
                </Button>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
